import { ReactNode } from 'react';
import styled from 'styled-components';
import { APIComponentAttributes, APIEntityType } from '@ardoq/api-types';
import { EnhancedScopeData } from '@ardoq/data-model';
import {
  ComponentRenderer,
  FieldType,
  RenderOption,
  RenderOptions,
  getFieldLabel,
  RedactedFieldDisplay,
} from '@ardoq/renderers';
import { Paragraph } from '@ardoq/typography';
import { r4 } from '@ardoq/design-tokens';
import { FlexBox } from '@ardoq/layout';
import {
  PermissionContext,
  permissionsOperations,
} from '@ardoq/access-control';

const renderOptions: RenderOptions = {
  types: {
    [FieldType.TEXT_AREA]: new Set([RenderOption.MARKDOWN]),
  },
  definitions: {},
};

const HoverBox = styled(FlexBox).attrs({
  gap: 'xsmall',
  width: 'full',
  justify: 'space-between',
  paddingX: 'xsmall',
  paddingY: 'small',
})`
  border-radius: ${r4};
  &:hover {
    background-color: rgba(241, 247, 254, 1);
  }
`;

const StyledFieldLabelRenderer = ({ fieldLabel }: { fieldLabel: string }) => (
  <Paragraph variant="text2Bold">{fieldLabel}</Paragraph>
);

type FieldRendererProps = {
  fieldName: string;
  component: APIComponentAttributes;
  scopeData: EnhancedScopeData;
  customValueRender?: ReactNode;
  permissionContext: PermissionContext;
};

const FieldRenderer = ({
  fieldName,
  component,
  scopeData,
  customValueRender,
  permissionContext,
}: FieldRendererProps) => {
  const users = Object.fromEntries(
    scopeData.users.map(user => [user._id, user])
  );

  const isConfidential = !permissionsOperations.canSeeConfidentialFieldByName(
    permissionContext,
    fieldName,
    component.rootWorkspace
  );

  const fieldLabel = getFieldLabel({
    fieldName,
    enhancedScopeData: scopeData,
    entityType: APIEntityType.COMPONENT,
  });

  return (
    <HoverBox role="group" aria-label={fieldLabel}>
      <StyledFieldLabelRenderer fieldLabel={fieldLabel} />
      {customValueRender ??
        (isConfidential ? (
          <RedactedFieldDisplay />
        ) : (
          <ComponentRenderer
            entityId={component._id}
            fieldName={fieldName}
            enhancedScopeData={scopeData}
            users={users}
            options={renderOptions}
          />
        ))}
    </HoverBox>
  );
};

export { FieldRenderer };
